import type { VersionStateType } from 'services/EpdClient';
import { BaseValidationRuleResolver } from './BaseValidationRuleResolver';

/** Must stay identical to VerifierAvailableValidationRuleResolver.Message on the backend. */
export const VERIFIER_NOT_AVAILABLE_MESSAGE =
  'This verifier is not available anymore. Please select an active verifier to continue.';

/**
 * VersionStateType.Draft and VersionStateType.DeclinedVerification. Held as type-checked literals
 * because importing the enum at runtime pulls the generated client (and its ESM axios import) into
 * every Jest suite that loads validation.ts.
 */
const asVersionState = (state: `${VersionStateType}`) => state as VersionStateType;
const STATES_ALLOWING_SEND_FOR_VERIFICATION: ReadonlyArray<VersionStateType | null> = [
  asVersionState('Draft'),
  asVersionState('Declined verification'),
];

/**
 * Portal twin of the backend "verifierAvailable" rule. Flags a selected verifier whose data source
 * option is disabled, but only while the version can still be sent for verification (Draft and
 * Declined verification). A value missing from the options is left to the generic in-options check.
 */
export const verifierAvailableValidationRuleResolver: BaseValidationRuleResolver = (
  value,
  _rule,
  _prop,
  path,
  context
) => {
  if (!STATES_ALLOWING_SEND_FOR_VERIFICATION.includes(context.versionState)) return null;

  if (typeof value !== 'string' || value === '') return null;

  const isDisabled = context.getOptions(path).some((option) => option.value === value && option.isDisabled === true);
  return isDisabled ? VERIFIER_NOT_AVAILABLE_MESSAGE : null;
};
